import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authUtils } from '../utils/auth';
import { posthogService } from '../services/posthogService';

interface AuthContextType {
  isAuthenticated: boolean;
  userName: string | null;
  loginAttempts: number;
  incrementLoginAttempts: () => void;
  resetLoginAttempts: () => void;
  login: (token: string, name: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(authUtils.isAuthenticated());
  const [userName, setUserName] = useState(authUtils.getUserName());
  const [loginAttempts, setLoginAttempts] = useState(0);

  useEffect(() => {
    // Check authentication status on mount and whenever localStorage changes
    const checkAuthStatus = () => {
      const authenticated = authUtils.isAuthenticated();
      const name = authUtils.getUserName();
      setIsAuthenticated(authenticated);
      setUserName(name);
    };

    checkAuthStatus();

    // Listen for storage changes (in case user logs out in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' || e.key === 'user_name') {
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const incrementLoginAttempts = () => {
    setLoginAttempts(prev => prev + 1);
  };

  const resetLoginAttempts = () => {
    setLoginAttempts(0);
  };

  const login = (token: string, name: string) => {
    authUtils.setToken(token);
    authUtils.setUserName(name);
    setIsAuthenticated(true);
    setUserName(name);
    resetLoginAttempts();

    // Identify user in PostHog for session recording
    const userEmail = authUtils.getEmail();
    if (userEmail) {
      posthogService.identifyUser(userEmail, {
        name: name,
      });
    }
  };

  const logout = () => {
    authUtils.clearAll();
    setIsAuthenticated(false);
    setUserName(null);

    // Reset PostHog session on logout
    posthogService.reset();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userName,
        loginAttempts,
        incrementLoginAttempts,
        resetLoginAttempts,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};